import React, { useRef } from 'react';
import { motion, useMotionValue, useTransform, useSpring, useScroll } from 'framer-motion';

/**
 * Tilt3D - Interactive 3D tilt component for cards that follows mouse movements
 */
export const Tilt3D = ({ children, className = '' }) => {
  const ref = useRef(null);
  
  // Mouse position percentages relative to card center [-0.5, 0.5]
  const x = useMotionValue(0);
  const y = useMotionValue(0);

  // Smooth springs for rotation
  const rotateX = useSpring(useTransform(y, [-0.5, 0.5], [12, -12]), { stiffness: 150, damping: 15 });
  const rotateY = useSpring(useTransform(x, [-0.5, 0.5], [-12, 12]), { stiffness: 150, damping: 15 });
  
  // Custom glowing reflection positions
  const glowX = useSpring(useTransform(x, [-0.5, 0.5], ["0%", "100%"]), { stiffness: 150, damping: 15 });
  const glowY = useSpring(useTransform(y, [-0.5, 0.5], ["0%", "100%"]), { stiffness: 150, damping: 15 });

  const handleMouseMove = (event) => {
    if (!ref.current) return;
    const rect = ref.current.getBoundingClientRect();
    const width = rect.width;
    const height = rect.height;
    
    // Calculate mouse position relative to card center
    const mouseX = event.clientX - rect.left - width / 2;
    const mouseY = event.clientY - rect.top - height / 2;
    
    x.set(mouseX / width);
    y.set(mouseY / height);
  };

  const handleMouseLeave = () => {
    x.set(0);
    y.set(0);
  };

  // Combine multiple values into a smooth gradient background for the overlay
  const glowStyle = {
    background: useTransform(
      [glowX, glowY],
      ([gx, gy]) => `radial-gradient(circle 120px at ${gx} ${gy}, rgba(0, 255, 136, 0.2), transparent 70%)`
    ),
  };

  return (
    <motion.div
      ref={ref}
      onMouseMove={handleMouseMove}
      onMouseLeave={handleMouseLeave}
      style={{
        rotateX,
        rotateY,
        transformStyle: "preserve-3d",
      }}
      className={`relative group ${className}`}
    >
      {/* Light glow reflection following the mouse */}
      <motion.div
        className="absolute inset-0 pointer-events-none rounded-3xl opacity-0 group-hover:opacity-100 transition-opacity duration-500 z-20"
        style={glowStyle}
      />
      {/* 3D Depth Card Inner Container */}
      <div style={{ transform: "translateZ(30px)", transformStyle: "preserve-3d" }} className="w-full h-full">
        {children}
      </div>
    </motion.div>
  );
};

/**
 * Scroll3DSection - Smooth cinematic 3D scrolling parallax animation
 * Causes section to rise, rotate, and scale in 3D space as it approaches the viewport center
 */
export const Scroll3DSection = ({ children, className = '' }) => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start end", "end start"]
  });

  // Calculate high definition 3D transformations linked directly to viewport scroll
  const rawRotateX = useTransform(scrollYProgress, [0, 0.4, 0.6, 1], [15, 0, 0, -15]);
  const rawScale = useTransform(scrollYProgress, [0, 0.4, 0.6, 1], [0.92, 1, 1, 0.92]);
  const rawOpacity = useTransform(scrollYProgress, [0, 0.15, 0.85, 1], [0, 1, 1, 0]);
  const rawTranslateY = useTransform(scrollYProgress, [0, 0.4, 0.6, 1], [80, 0, 0, -80]);

  // Spring physics variables
  const rotateX = useSpring(rawRotateX, { stiffness: 80, damping: 20, mass: 0.5 });
  const scale = useSpring(rawScale, { stiffness: 80, damping: 20, mass: 0.5 });
  const opacity = useSpring(rawOpacity, { stiffness: 80, damping: 20, mass: 0.5 });
  const translateY = useSpring(rawTranslateY, { stiffness: 80, damping: 20, mass: 0.5 });

  return (
    <div ref={ref} style={{ perspective: "2000px" }} className="overflow-visible w-full">
      <motion.div
        style={{
          rotateX,
          scale,
          opacity,
          y: translateY,
          transformStyle: "preserve-3d",
        }}
        className={className}
      >
        {children}
      </motion.div>
    </div>
  );
};
